import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Calculator, Download, LogOut } from "lucide-react";
import Seo from "@/components/Seo";
import { api, authHeaders, clearToken, getToken, money } from "@/lib/solarApi";
import { CUSTOMER_TYPES } from "@/components/solar/SolarAuth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_CHIP = {
  paid: "bg-emerald-50 text-emerald-700 border-emerald-300",
  pending: "bg-amber-50 text-amber-700 border-amber-300",
  failed: "bg-red-50 text-red-700 border-red-300",
  refunded: "bg-slate-100 text-slate-600 border-slate-300",
};

const fieldCls = "h-11 bg-white border-slate-300 text-slate-900 focus-visible:ring-emerald-600";

export default function SolarDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/solar/my", { headers: authHeaders() });
      let energy = { plans: [], assessments: [], support: [] };
      try {
        const res = await api.get("/energy/my", { headers: authHeaders() });
        energy = res.data;
      } catch { /* energy section optional */ }
      setData({ ...data, energy });
      setProfile({
        name: data.user.name || "", phone: data.user.phone || "",
        country: data.user.country || "", city: data.user.city || "",
        customer_type: data.user.customer_type || "residential",
      });
    } catch {
      clearToken();
      navigate("/solar/calculator");
    }
  }, [navigate]);

  useEffect(() => {
    if (!getToken()) { navigate("/solar/calculator"); return; }
    load();
  }, [load, navigate]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/auth/me", profile, { headers: authHeaders() });
      toast.success("Contact information updated");
    } catch {
      toast.error("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async (ref) => {
    try {
      const res = await api.get(`/solar/orders/${ref}/pdf`, { headers: authHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `BNM-Solar-Design-${ref}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Report is locked until payment is confirmed.");
    }
  };

  const logout = () => { clearToken(); navigate("/solar"); };

  return (
    <>
      <Seo title="My Solar Dashboard — BNM Sustainable Energy Limited" description="Your BNM solar calculations, orders and reports." />
      <section data-testid="solar-dashboard" className="bg-mist grid-lines-light min-h-screen">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 lg:pt-32 pb-24">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-emerald-600">My Account</p>
              <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-slate-900">
                Solar Dashboard
              </h1>
              {data && <p className="mt-1 text-sm text-slate-500">{data.user.email}</p>}
            </div>
            <div className="flex gap-2">
              <Link to="/solar/calculator" data-testid="dash-new-calc" className="inline-flex items-center gap-2 rounded-md bg-obsidian px-5 py-3 text-sm font-semibold text-white hover:bg-pine hover:text-volt transition-colors">
                <Calculator className="h-4 w-4" aria-hidden="true" /> New calculation
              </Link>
              <button onClick={logout} data-testid="dash-logout" className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-600 hover:text-red-600 hover:border-red-300 transition-colors">
                <LogOut className="h-4 w-4" aria-hidden="true" /> Log out
              </button>
            </div>
          </div>

          {error && <p role="alert" className="mt-6 text-sm text-red-600">{error}</p>}
          {!data && !error && <p className="mt-16 text-center text-sm text-slate-500">Loading…</p>}

          {data && (
            <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <div className="rounded-lg border border-slate-200 bg-white p-6">
                  <h2 className="font-display text-lg font-bold text-slate-900">Orders & Reports</h2>
                  {data.orders.length === 0 && (
                    <p className="mt-4 text-sm text-slate-500">No orders yet — run a calculation to get started.</p>
                  )}
                  <ul className="mt-4 space-y-3">
                    {data.orders.map((o) => (
                      <li key={o.reference} data-testid={`dash-order-${o.reference}`} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-200 bg-mist p-4">
                        <div>
                          <p className="font-mono text-xs font-semibold text-slate-900">{o.reference}</p>
                          <p className="mt-0.5 text-xs text-slate-500">{money(o.amount, o.currency)} · {new Date(o.created_at).toLocaleDateString()}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.12em] ${STATUS_CHIP[o.payment_status] || STATUS_CHIP.pending}`}>
                            {o.payment_status}
                          </span>
                          {o.payment_status === "paid" ? (
                            <>
                              <Link to={`/solar/report?ref=${o.reference}`} data-testid={`dash-view-${o.reference}`} className="rounded-md border border-emerald-400 px-3.5 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-50">View</Link>
                              <button onClick={() => downloadPdf(o.reference)} data-testid={`dash-pdf-${o.reference}`} className="inline-flex items-center gap-1.5 rounded-md bg-obsidian px-3.5 py-2 text-xs font-semibold text-white hover:bg-pine">
                                <Download className="h-3.5 w-3.5" aria-hidden="true" /> PDF
                              </button>
                            </>
                          ) : (
                            <Link to={`/solar/report?ref=${o.reference}`} data-testid={`dash-status-${o.reference}`} className="rounded-md border border-slate-300 px-3.5 py-2 text-xs font-semibold text-slate-600 hover:border-emerald-400">
                              Check status
                            </Link>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="rounded-lg border border-slate-200 bg-white p-6">
                  <h2 className="font-display text-lg font-bold text-slate-900">My Energy Plans</h2>
                  {(!data.energy?.plans || data.energy.plans.length === 0) && (
                    <p className="mt-4 text-sm text-slate-500">No saved energy plans yet — use the Energy Engine and choose "Save to my dashboard".</p>
                  )}
                  <ul className="mt-4 space-y-3">
                    {(data.energy?.plans || []).map((p) => (
                      <li key={p.id} data-testid={`dash-plan-${p.id}`} className="rounded-md border border-slate-200 bg-mist p-4">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-slate-900">
                            {p.plan?.energy?.daily_kwh} kWh/day · {p.plan?.solar?.installed_kw} kW solar · {p.plan?.battery?.installed_kwh} kWh battery
                          </p>
                          <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500">{new Date(p.created_at).toLocaleString()}</p>
                        </div>
                        <p className="mt-1 text-xs text-slate-500">
                          {p.plan?.goal_label} · {(p.profile?.location || p.profile?.city) || "—"} · Est. SLE {(p.plan?.costs?.total_sle || 0).toLocaleString()}
                        </p>
                      </li>
                    ))}
                  </ul>
                  <Link to="/energy/engine" data-testid="dash-energy-engine" className="mt-4 inline-block text-sm font-semibold text-emerald-700 hover:underline">
                    Open the Energy Engine →
                  </Link>
                </div>

                <div className="rounded-lg border border-slate-200 bg-white p-6">
                  <h2 className="font-display text-lg font-bold text-slate-900">My Calculations</h2>
                  {data.calculations.length === 0 && (
                    <p className="mt-4 text-sm text-slate-500">No calculations yet.</p>
                  )}
                  <ul className="mt-4 space-y-3">
                    {data.calculations.map((c) => (
                      <li key={c.id} data-testid={`dash-calc-${c.id}`} className="rounded-md border border-slate-200 bg-mist p-4">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-slate-900">{c.item_count} appliances · {c.results?.free?.daily_kwh} kWh/day</p>
                          <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500">{new Date(c.created_at).toLocaleString()}</p>
                        </div>
                        <p className="mt-1 text-xs text-slate-500">Peak {c.results?.free?.peak_w} W · Solar range {c.results?.free?.solar_range}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 bg-white p-6 h-fit">
                <h2 className="font-display text-lg font-bold text-slate-900">Contact Information</h2>
                {profile && (
                  <form onSubmit={saveProfile} className="mt-5 space-y-4" data-testid="dash-profile-form">
                    <div>
                      <Label htmlFor="dp-name">Full name</Label>
                      <Input id="dp-name" className={fieldCls} value={profile.name} onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))} />
                    </div>
                    <div>
                      <Label htmlFor="dp-phone">WhatsApp / phone</Label>
                      <Input id="dp-phone" className={fieldCls} value={profile.phone} onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))} />
                    </div>
                    <div>
                      <Label htmlFor="dp-country">Country</Label>
                      <Input id="dp-country" className={fieldCls} value={profile.country} onChange={(e) => setProfile((p) => ({ ...p, country: e.target.value }))} />
                    </div>
                    <div>
                      <Label htmlFor="dp-city">City / town</Label>
                      <Input id="dp-city" className={fieldCls} value={profile.city} onChange={(e) => setProfile((p) => ({ ...p, city: e.target.value }))} />
                    </div>
                    <div>
                      <Label htmlFor="dp-type">Customer type</Label>
                      <Select value={profile.customer_type} onValueChange={(v) => setProfile((p) => ({ ...p, customer_type: v }))}>
                        <SelectTrigger id="dp-type" className={fieldCls}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {CUSTOMER_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <button type="submit" disabled={saving} data-testid="dash-save-profile" className="w-full rounded-md bg-obsidian px-5 py-3 text-sm font-semibold text-white hover:bg-pine hover:text-volt transition-colors disabled:opacity-60">
                      {saving ? "Saving…" : "Save changes"}
                    </button>
                  </form>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
